import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useClickOutside from "../../hooks/useClickOutside";
import useResolvedModel from "../../hooks/useResolvedModel";

/**
 * EditorModelSelector — compact model pill + anchored popover for the inline
 * AI editor toolbar.
 *
 * - Defaults to the current chat's resolved model (same priority chain as the
 *   main PromptModelSelector), giving the user a consistent starting point.
 * - Persists the user's override to localStorage keyed by artifact language so
 *   the preference is recalled across sessions (e.g. always use Gemini for JS).
 * - Calls onChange(modelId) whenever the selection changes (including on the
 *   initial resolution) so the parent can pass model_id to startEdit().
 *
 * Props:
 *   language    {string}    Artifact language / type (e.g. "html", "javascript")
 *   chatId      {string}    Current chat id — used to derive the default model
 *   onChange    {function}  Called with the selected model id whenever it changes
 */
const STORAGE_KEY = (language) =>
  `nebula_editor_model_${(language || "unknown").toLowerCase()}`;

const EditorModelSelector = ({ language, chatId, onChange }) => {
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    openUpward: true,
  });

  const { resolvedModel, isLoading, options } = useResolvedModel({
    chat_id: chatId,
    space: null,
  });

  useClickOutside([dropdownRef, buttonRef], () => setIsOpen(false));

  // Once options are available, resolve the initial selection:
  // localStorage override > chat's resolved model > first option
  useEffect(() => {
    if (!options || options.length === 0) return;

    const stored = localStorage.getItem(STORAGE_KEY(language));
    const fromStorage = stored ? options.find((m) => m.id === stored) : null;
    const initial = fromStorage ?? resolvedModel ?? options[0] ?? null;

    if (initial && initial.id !== selectedModel?.id) {
      setSelectedModel(initial);
      onChange?.(initial.id);
    }
    // Intentionally omit selectedModel from deps — we only want to run this
    // when the data source settles, not on every local selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, resolvedModel, language]);

  // Recompute dropdown anchor position each time the popover opens
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const DROPDOWN_MAX_HEIGHT = 280;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward =
      spaceAbove >= DROPDOWN_MAX_HEIGHT || spaceAbove > spaceBelow;
    setDropdownPosition({
      top: rect.bottom,
      bottom: window.innerHeight - rect.top,
      left: rect.left,
      openUpward,
    });
  }, [isOpen]);

  const handleSelect = (model) => {
    setSelectedModel(model);
    localStorage.setItem(STORAGE_KEY(language), model.id);
    onChange?.(model.id);
    setIsOpen(false);
  };

  if (isLoading || !options || options.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((v) => !v)}
        title="Select model for AI edit"
        className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium bg-fill-navigation-hover dark:bg-fill-navigation-active hover:bg-fill-gray-200 dark:hover:bg-fill-sidebar-surface-dark text-ink-gray-700 dark:text-ink-gray-200 transition-colors duration-150 min-w-0 flex-shrink-0"
      >
        <span className="truncate max-w-[80px]">
          {selectedModel ? selectedModel.name : "Model"}
        </span>
        <ChevronDown
          className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: dropdownPosition.openUpward ? 8 : -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: dropdownPosition.openUpward ? 8 : -8 }}
              transition={{ duration: 0.13 }}
              style={{
                position: "fixed",
                ...(dropdownPosition.openUpward
                  ? { bottom: `${dropdownPosition.bottom + 4}px` }
                  : { top: `${dropdownPosition.top + 4}px` }),
                left: `${dropdownPosition.left}px`,
                width: "180px",
                zIndex: 9999,
              }}
              className="max-h-72 overflow-y-auto bg-fill-white dark:bg-fill-panel-surface border border-edge-gray-200 dark:border-edge-panel-border rounded-lg shadow-lg"
            >
              <div className="p-1">
                <div className="px-2 py-1 text-[10px] font-semibold text-ink-gray-500 dark:text-ink-gray-400 border-b border-edge-gray-200 dark:border-edge-panel-border mb-1">
                  AI Edit Model
                </div>
                <div className="space-y-0.5">
                  {options.map((option) => {
                    const isSelected = selectedModel?.id === option.id;
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleSelect(option)}
                        className={`w-full text-left rounded-md px-2 py-1.5 text-[11px] transition-colors duration-150 ${
                          isSelected
                            ? "bg-fill-sidebar-surface dark:bg-fill-blue-900/20 border border-edge-primary/20 dark:border-edge-accent/20 text-ink-primary dark:text-ink-accent font-medium"
                            : "hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 text-ink-gray-700 dark:text-ink-gray-200"
                        }`}
                      >
                        <span className="truncate block">{option.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default EditorModelSelector;
